<template>

  <dialog id="spot_preview_modal"
          class="modal min-h-[500px]">
    <div class="modal-box rounded-none px-1 py-0">
      <form method="dialog">
      </form>
      <SpotPreview v-if="spotPreview"
                   :spotPreview="spotPreview" />
      <ErrorView v-if="detailError"
                 :error="detailError" />
    </div>
    <form method="dialog"
          class="modal-backdrop">
      <button>close</button>
    </form>
  </dialog>
  <dialog id="map_sponsor_modal"
          class="modal min-h-[500px]">
    <div class="modal-box rounded-none p-0">
      <form method="dialog">
      </form>
      <MapSponsor v-if="sponsor"
                  :sponsor="sponsor"></MapSponsor>
    </div>
    <form method="dialog"
          class="modal-backdrop">
      <button>close</button>
    </form>
  </dialog>
  <div class="absolute">
    <div v-if="showControls"
         class="relative sm:left-1 sm:top-1">
      <ButtonBright @click="openFilterBar()"
                    class="z-30 absolute text-nowrap left-[9px] top-[13px]"
                    :class="filtersSet ? 'bg-theme-secondary text-white' : 'bg-white'">{{
          $t('spots.filter')
        }}<span v-if="filtersSet"
                class="inline-block bg-theme-primary h-[20px] w-[20px] rounded-[10px] ml-1 text-black">{{
            numberOfFilters
          }}</span>
      </ButtonBright>
      <div :class="{ hidden: !filterBarVisible }"
           class="absolute w-screen sm:w-[600px] shadow-lg bg-theme-primary z-50">
        <FilterBar ref="listFilterBar"
                   class=" w-full"
                   @submit="applyFilters"
                   @close="closeFilterBar"
                   :map="true" />
      </div>
    </div>
  </div>
  <div class="absolute right-0"
       v-if="authenticated && showControls">
    <div class="relative right-1 top-1 sm:right-2 sm:top-2">
      <div class="flex gap-2">
        <MapSearch class="z-40"
                   :goto-address="gotoAddress" />
      </div>
    </div>
  </div>
  <div v-if="mapError"
       class="absolute top-0 min-h-full min-w-full flex flex-col justify-center">
    <ErrorView :error="mapError"
               class="z-40" />
  </div>
  <div id="map"
       class="z-30"
       :style="mapStyle">
  </div>
</template>
<script setup lang="ts">

import { Loader } from '@googlemaps/js-api-loader';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import type { Feature, Point } from 'geojson';
import { FetchError } from 'ofetch';

const props = defineProps<{
  platformName: string,
  countryCode: string,
  initialMapBounds?: MapBounds,
  mapStyle: string,
  spotLocations: Feature<Point>[],
  showControls?: boolean,
  useClusterer?: boolean,
  embedFilterParams?: any,
  showSponsors?: boolean,
  zoomControl?: boolean,
  mapTypeControl?: boolean,
  scaleControl?: boolean,
  streetViewControl?: boolean
  region?: Feature
}>();

const markerSize = ref<'regular' | 'small' | 'tiny'>('tiny');
const spotPreview = ref<SpotPreview>();
const sponsor = ref<MapSponsor>();
const listFilterBar = ref();
const { getItems } = useDirectusItems();
const authenticated = useAuth().authenticated;

const filterBarVisible = ref(false);
const filterParams = ref<any>();
const filtersSet = ref<boolean>(false);
const numberOfFilters = ref<number>(0);

let map: google.maps.Map;
let spotMarkers = new Array<MoveableSpotMarker>();
let sponsorMarkers = new Array<SponsorMarker>();
let clusterer: MarkerClusterer;
const switchToCluster = 13;

const googleMapBounds = ref<MapBounds | undefined>();

const mapError = ref<FetchError | undefined>(undefined);
const detailError = ref<FetchError | undefined>(undefined);

onMounted(() => {
  const runtimeConfig = useRuntimeConfig();
  const apiKey = runtimeConfig.public.GOOGLE_MAPS_API_KEY as string;
  const loader = new Loader({
    apiKey: apiKey
  });
  initMap(loader);
});

async function loadMarkers() {
  spotMarkers = [];
  sponsorMarkers = [];
  try {
    const spotMarkerData = props.spotLocations;
    if (spotMarkerData) {
      spotMarkerData.forEach((feature) => {
        createMoveableSpotMarker(feature);
      });
    }
  } catch (e) {
    mapError.value = e;
  }

  if (props.showSponsors) {
    try {
      const sponsorMarkerData = await getItems<MapSponsorPoint>({
        collection: 'd_map_sponsor',
        params: {
          fields: ['id', 'location'],
          filter: {
            platform: {
              d_platform_id: {
                name: props.platformName
              }
            }
          }
        }
      });
      sponsorMarkerData.forEach((sponsor) => {
        const coordinates = sponsor.location.coordinates;
        const lat = coordinates[1];
        const lon = coordinates[0];
        const marker = createSponsorMarker(lat, lon, sponsor.id);
        sponsorMarkers.push(marker);
      });
    } catch (e) {
      console.log(e);
    }
  }

  if (props.useClusterer) {
    clusterer.clearMarkers();
    clusterer.addMarkers(spotMarkers);
    clusterer.addListener('click', (event: Event) => {
      adjustVisibilityOfMapContent();
    });
  } else {
    spotMarkers.forEach((marker) => {
      marker.map = map;
    });
  }
  sponsorMarkers.forEach((marker) => {
    marker.map = map;
  });

  adjustVisibilityOfMapContent();
}

const gotoAddress = (lat: number, lon: number) => {
  map.setCenter({ lat: lat, lng: lon });
  map.setZoom(15);
};

async function applyFilters(newFilterParams: any) {
  filterBarVisible.value = false;
  filterParams.value = newFilterParams;
  await loadMarkers();
  filtersSet.value = Object.keys(newFilterParams).length - Object.keys(props.embedFilterParams).length > 0;
  numberOfFilters.value = Object.keys(newFilterParams).length - Object.keys(props.embedFilterParams).length;
}

function closeFilterBar() {
  listFilterBar.value.setFilters(filterParams.value);
  filterBarVisible.value = false;
}

function openFilterBar() {
  filterBarVisible.value = true;
}

function hideMarkers() {
  spotMarkers.forEach((marker) => {
    marker.map = null;
  });
}

async function loadSpotPreview(id: number) {
  spotPreview.value = undefined;

  try {
    spotPreview.value = await useApi().get<SpotPreview>('spotPreviews/' + String(id));
  } catch (e) {
    detailError.value = e;
  }
}

async function loadSponsorDetail(id: number) {
  sponsor.value = undefined;
  const sponsors = await getItems<MapSponsor>({
    collection: 'd_map_sponsor',
    params: {
      fields: ['*'],
      filter: {
        id: id
      }
    }
  });
  sponsor.value = sponsors[0];

}

function createMoveableSpotMarker(feature: any) {
  const coordinates = feature.geometry.coordinates;
  const lat = coordinates[1];
  const lon = coordinates[0];

  const id = feature.properties.id;
  const famed = feature.properties.famed;
  const gotFixed = feature.properties.fixed;
  const marker = createSpotMarker(lat, lon, id, famed, gotFixed);

  const moveableSpotMarker = marker as MoveableSpotMarker;
  moveableSpotMarker.famed = famed;
  moveableSpotMarker.fixed = gotFixed;
  spotMarkers.push(moveableSpotMarker);
}

function spotIcon(famed: boolean, fixed: boolean, size: 'regular' | 'small' | 'tiny'): HTMLElement {
  const iconElement = document.createElement('img');

  iconElement.src = fixed
    ? '/icons/mapmarker/' + size + '-fixed.png'
    : famed
      ? '/icons/mapmarker/' + size + '-fame.png'
      : '/icons/mapmarker/' + size + '-shame.png';

  const iconWidth = size === 'regular' ? 18 : size === 'small' ? 8 : 4;
  const iconHeight = size === 'regular' ? 24 : size === 'small' ? 8 : 4;

  iconElement.width = iconWidth;
  iconElement.height = iconHeight;

  return iconElement;
}

function sponsorIcon(size: 'regular' | 'small' | 'tiny'): HTMLElement {
  const iconElement = document.createElement('img');

  iconElement.src = '/icons/mapmarker/' + size + '-star.png';

  const iconWidth = size === 'regular' ? 17 : size === 'small' ? 8 : 4;
  const iconHeight = size === 'regular' ? 16 : size === 'small' ? 8 : 4;

  iconElement.width = iconWidth;
  iconElement.height = iconHeight;

  return iconElement;
}

function createSpotMarker(
  lat: number,
  lon: number,
  id: number,
  famed: boolean,
  gotFixed: boolean
): MoveableSpotMarker {

  const icon = spotIcon(famed, gotFixed, markerSize.value);
  const marker = new google.maps.marker.AdvancedMarkerElement({
    content: icon,
    position: { lat: lat, lng: lon }
  });

  marker.addListener('click', () => {
    loadSpotPreview(id);
    (document.getElementById('spot_preview_modal') as HTMLFormElement).showModal();
  });

  return marker as MoveableSpotMarker;
}

function createSponsorMarker(
  lat: number,
  lon: number,
  id: number
): SponsorMarker {
  const icon = document.createElement('img');
  icon.src = '/icons/mapmarker/tiny-star.png';
  icon.width = 4;
  icon.height = 4;
  const marker = new google.maps.marker.AdvancedMarkerElement({
    content: icon,
    position: { lat: lat, lng: lon }
  });

  marker.addListener('click', () => {
    loadSponsorDetail(id);
    (document.getElementById('map_sponsor_modal') as HTMLFormElement).showModal();
  });

  return marker as SponsorMarker;
}

function setMarkerIcons(size: 'regular' | 'small' | 'tiny') {
  spotMarkers.forEach((marker) => {
    marker.content = spotIcon(marker.famed, marker.fixed, size);
  });
}

function setSponsorIcons(size: 'regular' | 'small' | 'tiny') {
  sponsorMarkers.forEach((marker) => {
    marker.content = sponsorIcon(size);
  });

}


function adjustVisibilityOfMapContentWithParameters(switchFromSmallIconsToRegularIcons: number, switchFromTinyIconsToSmallIcons: number) {
  const zoom = map.getZoom();

  if (zoom) {
    if (zoom >= switchFromSmallIconsToRegularIcons) {
      if (markerSize.value !== 'regular') {
        markerSize.value = 'regular';
        setMarkerIcons('regular');
        setSponsorIcons('regular');
      }
    } else if ((zoom >= switchFromTinyIconsToSmallIcons)) {
      if (markerSize.value !== 'small') {
        markerSize.value = 'small';
        setMarkerIcons('small');
        setSponsorIcons('small');
      }
    } else {
      if (markerSize.value !== 'tiny') {
        markerSize.value = 'tiny';
        setMarkerIcons('tiny');
        setSponsorIcons('tiny');
      }
    }
  }
}

function adjustVisibilityOfMapContent() {
  if (props.useClusterer) {
    adjustVisibilityOfMapContentWithParameters(15, 14);
  } else {
    adjustVisibilityOfMapContentWithParameters(13, 11);
  }
}

function saveMapBounds() {
  if (map.getBounds()) {
    googleMapBounds.value = map.getBounds();
  }
}

function initialmapBoundsFromLocations(): MapBounds {
  if (props.spotLocations) {
    const lat = props.spotLocations[0].geometry.coordinates[1];
    const lon = props.spotLocations[0].geometry.coordinates[0];
    let swLat = lat;
    let swLon = lon;
    let neLat = lat;
    let neLon = lon;

    for (const spotLocation of props.spotLocations) {
      swLat = Math.min(swLat, spotLocation.geometry.coordinates[1]);
      swLon = Math.min(swLon, spotLocation.geometry.coordinates[0]);
      neLat = Math.max(neLat, spotLocation.geometry.coordinates[1]);
      neLon = Math.max(neLon, spotLocation.geometry.coordinates[0]);
    }

    return {
      swLat: swLat,
      swLon: swLon,
      neLat: neLat,
      neLon: neLon
    };
  } else {
    return {
      swLat: 0,
      swLon: 0,
      neLat: 0,
      neLon: 0
    };
  }
}

async function initMap(loader: Loader) {
  await loader.importLibrary('maps');
  // Request needed libraries.
  const { Map, InfoWindow } = (await google.maps.importLibrary(
    'maps'
  )) as google.maps.MapsLibrary;
  const { AdvancedMarkerElement } = (await google.maps.importLibrary(
    'marker'
  )) as google.maps.MarkerLibrary;

  if (!googleMapBounds.value) {
    googleMapBounds.value = new google.maps.LatLngBounds();
    const theMapBounds = props.initialMapBounds;
    googleMapBounds.value.extend({ lat: theMapBounds.swLat, lng: theMapBounds.swLon });
    googleMapBounds.value.extend({ lat: theMapBounds.neLat, lng: theMapBounds.neLon });
  }

  map = new Map(document.getElementById('map') as HTMLElement, {
    mapId: props.platformName === 'bikeable' ? 'c86932714dc4b6d7' : 'bcdaeb05cd4b3dfe',
    zoomControl: props.zoomControl,
    mapTypeControl: props.mapTypeControl,
    scaleControl: props.scaleControl,
    streetViewControl: props.streetViewControl,
    rotateControl: false,
    fullscreenControl: false
  });
  if (props.region) {
    map.data.addGeoJson(props.region);
    map.data.setStyle({
      fillColor: props.platformName === 'bikeable' ? '#33BDCA' : '#00AF7D',
      fillOpacity: 0.1,
      strokeColor: props.platformName === 'bikeable' ? '#33BDCA' : '#00AF7D',
      strokeWeight: 1
    });
  }

  map.addListener('idle', () => {
    if (props.useClusterer) {
      clusterer.clearMarkers();
      clusterer.addMarkers(spotMarkers.filter(marker =>
        map.getBounds()?.contains(marker.position!)
      ));
    }
    saveMapBounds();
    map.fitBounds(googleMapBounds.value, 0);
    adjustVisibilityOfMapContent();
  });

  if (props.useClusterer) {
    const renderer = {
      render: ({ count, position }) => {
        const p = document.createElement('p');
        let divStyle = `background: url("/icons/cluster-circle-${props.platformName}.svg") no-repeat center center; background-size: fill;`;
        let size;
        let fontSize;

        if (count < 10) {
          size = 20;
          fontSize = 10;
        } else if (count < 100) {
          size = 25;
          fontSize = 12;
        } else if (count < 1000) {
          size = 35;
          fontSize = 12;
        } else {
          size = 40;
          fontSize = 12;
        }

        divStyle += `width: ${size}px; height: ${size}px; font-size: ${fontSize}px;`;
        const pStyle = `font-size: ${fontSize}px; color:white; text-align: center`;
        p.setAttribute('style', pStyle);
        p.innerHTML = count;
        const div = document.createElement('div');
        div.setAttribute('style', divStyle);
        div.setAttribute('class', 'flex justify-center items-center');
        div.appendChild(p);
        return new google.maps.marker.AdvancedMarkerElement({
          content: div,
          position,
          // adjust zIndex to be above other markers
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count
        });
      }
    };
    const algorithm = new SuperClusterAlgorithm({ maxZoom: switchToCluster, radius: 100 });
    clusterer = new MarkerClusterer({ map: map, renderer: renderer, algorithm: algorithm });
  }
  await loadMarkers();

  adjustVisibilityOfMapContent();

}
</script>
